"use client";

import { useNumComponentsByClientSize } from "@faire/deprecated-retailer-visitor-shared/components/UPLT/useNumComponentsByClientSize";
import { useTrackDebugEventLoggingCallback } from "@faire/deprecated-retailer-visitor-shared/components/UPLT/useTrackDebugEventLoggingCallback";
import { getUser } from "@faire/deprecated-retailer-visitor-shared/serialized-data/getUser";
import { useFrontendSetting } from "@faire/deprecated-retailer-visitor-shared/settings/useFrontendSetting";
import { FE_ENABLE_UPLT_DEBUG_LOGGING } from "@faire/retailer-settings/FE_ENABLE_UPLT_DEBUG_LOGGING";
import { UserPerceivedLoadTimingComponentsPerClientSize } from "@faire/web/common/performance/userPerceivedLoadTiming//calculateNumComponentsPerClientSize";
import { useDefaultTrackResultCallback } from "@faire/web/common/performance/userPerceivedLoadTiming/useDefaultTrackResultCallback";
import { UserPerceivedLoadTimingContext } from "@faire/web/common/performance/userPerceivedLoadTiming/UserPerceivedLoadTimingContext";
import {
  UPLTHookOptions,
  useUserPerceivedLoadTimingWithKey,
} from "@faire/web/common/performance/userPerceivedLoadTiming/useUserPerceivedLoadTiming";
import { useWaitForImagesToLoad } from "@faire/web/common/performance/useWaitForImagesToLoad/useWaitForImagesToLoad";
import { useMergedRefs } from "@faire/web/ui/hooks/useMergedRefs";
import React, { useCallback } from "react";

export interface UPLTWrapperProps
  extends Omit<UPLTHookOptions, "numComponents"> {
  children?: React.ReactNode;
  numComponentsPerClientSize?: UserPerceivedLoadTimingComponentsPerClientSize;
  shouldUseWaitForImagesToLoadPromise?: boolean;
  onImageLoad?: () => void;
}

interface UPLTWrapperBaseProps extends UPLTWrapperProps {
  userToken: string;
}

const IMAGE_PERCENTAGE_REMAINING = 0.1;
const IMAGE_COUNT_REMAINING = 1;

const IMAGE_LOADING_TIMEOUT = 7_000;

export const UPLTWrapper: React.FC<UPLTWrapperProps> = (props) => {
  const enableDebugEventLogging = useFrontendSetting(
    FE_ENABLE_UPLT_DEBUG_LOGGING
  )?.includes(props.name);
  const userToken = getUser()?.token ?? "";

  if (enableDebugEventLogging) {
    return <UPLTWrapperBaseWithDebugLogging {...props} userToken={userToken} />;
  }
  return <UPLTWrapperBase {...props} userToken={userToken} />;
};

const UPLTWrapperSharedBase: React.FC<UPLTWrapperBaseProps> = ({
  children,
  numComponentsPerClientSize,
  shouldUseWaitForImagesToLoadPromise,
  onImageLoad,
  userToken,
  ...rest
}) => {
  "use no memo";
  const renderNonPrimaryContentResolveRef = React.useRef<
    (() => void) | undefined
  >(undefined);
  const [renderNonPrimaryContentPromise] = React.useState<Promise<void>>(
    () =>
      new Promise((resolve) => {
        if (!renderNonPrimaryContentResolveRef.current) {
          renderNonPrimaryContentResolveRef.current = resolve;
        }
      })
  );
  const numComponents = useNumComponentsByClientSize(
    numComponentsPerClientSize
  );
  const loadTimingRef = useUserPerceivedLoadTimingWithKey({
    ...rest,
    numComponents,
    trackResultCallback: useDefaultTrackResultCallback(userToken ?? ""),
  });

  const handleRenderNonPrimaryContent = useCallback(() => {
    renderNonPrimaryContentResolveRef.current?.();
  }, []);

  const handleImageLoad = useCallback(
    (numImagesPending: number, totalNumImages: number) => {
      if (
        numImagesPending < IMAGE_COUNT_REMAINING ||
        numImagesPending / totalNumImages < IMAGE_PERCENTAGE_REMAINING
      ) {
        handleRenderNonPrimaryContent();
        onImageLoad?.();
      }
    },
    [handleRenderNonPrimaryContent, onImageLoad]
  );

  const waitForImagesToLoadRef = useWaitForImagesToLoad({
    numComponents: numComponents ?? Infinity,
    onImageLoad: handleImageLoad,
    onTimeout: handleRenderNonPrimaryContent,
    timeout: IMAGE_LOADING_TIMEOUT,
  });

  const ref = useMergedRefs(
    loadTimingRef,
    shouldUseWaitForImagesToLoadPromise ? waitForImagesToLoadRef : undefined
  );

  return (
    <UserPerceivedLoadTimingContext.Provider
      value={{
        refCallback: ref,
        numComponents,
        renderNonPrimaryContentPromise,
      }}
    >
      {children}
    </UserPerceivedLoadTimingContext.Provider>
  );
};

const UPLTWrapperBase: React.FC<UPLTWrapperBaseProps> = (props) => {
  return <UPLTWrapperSharedBase {...props} />;
};

const UPLTWrapperBaseWithDebugLogging: React.FC<UPLTWrapperBaseProps> = (
  props
) => {
  return (
    <UPLTWrapperSharedBase
      {...props}
      trackDebugEventLoggingCallback={useTrackDebugEventLoggingCallback({
        name: props.name,
        userToken: props.userToken,
      })}
    />
  );
};
